/**
 * Lädt die Preisautomatik-Schicht – nur für WorkspaceAdmin und Staff, sonst keine Anfrage. Der
 * Kalender kommt mit `days=60` ab dem ersten sichtbaren Tag (trägt Grundkalender und Monat), die
 * Modi aus der Übersicht. Scheitert das Laden, verschwindet die Schicht nicht still: der Status
 * nennt den Grund, die zuletzt geladenen Daten bleiben. Solange etwas gesendet und unbestätigt
 * ist, fragt die Schicht nach.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { problemText, useApi, useApiProblem } from '@/shared/api';
import { hasAnyRole, ROLE_GROUPS, useAuth } from '@/shared/auth';
import type { AutomationStatus } from './AutomationLayerContext';
import { buildAutomationLayer, type AutomationLayer } from './automationModel';

const OPEN_PUBLICATION_POLL_MS = 10_000;

export function useAutomationLayerQuery(
  propertyGroupId: string | undefined,
  from: string,
): { layer: AutomationLayer | null; status: AutomationStatus | null } {
  const api = useApi();
  const { user } = useAuth();
  // Dieselben Rollen wie die Preisautomatik-Seiten (`PRICING_ROUTE_ROLES`).
  const enabled = Boolean(propertyGroupId) && hasAnyRole(user, ...ROLE_GROUPS.staff);
  const calendar = useQuery({
    queryKey: ['dynamic-pricing-calendar', propertyGroupId, from, 60, null],
    queryFn: () => api.dynamicPricingCalendar.get(propertyGroupId!, from, 60),
    enabled,
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.status !== 'error' &&
      query.state.data?.apartments.some((apartment) =>
        apartment.days.some(
          (day) =>
            day.publication &&
            !['Confirmed', 'Failed', 'ConfirmationFailed'].includes(day.publication.status),
        ),
      )
        ? OPEN_PUBLICATION_POLL_MS
        : false,
  });
  const overview = useQuery({
    queryKey: ['dynamic-pricing-overview', 'modes'],
    queryFn: () => api.dynamicPricingOverview.get(),
    enabled,
    staleTime: 60_000,
  });
  const problem = useApiProblem(calendar.error ?? overview.error);
  const failed = Boolean(calendar.error ?? overview.error);
  const layer = useMemo(
    () => (enabled && calendar.data ? buildAutomationLayer(calendar.data, overview.data) : null),
    [enabled, calendar.data, overview.data],
  );
  if (!enabled) return { layer: null, status: null };
  return {
    layer,
    status: failed
      ? {
          message: problemText(problem, 'Die Preisautomatik konnte nicht geladen werden.'),
          stale: layer !== null,
          retrying: calendar.isFetching || overview.isFetching,
          retry: () => {
            void calendar.refetch();
            void overview.refetch();
          },
        }
      : null,
  };
}
